import React, { useState } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonIcon,
  IonProgressBar
} from '@ionic/react';
import {
  trophy,
  checkmarkCircle,
  flame,
  star,
  code,
  sparkles
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../hooks/useConfig';
import { useProgress } from '../hooks/useProgress';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './Challenges.css';

/**
 * Challenges Page - Beautified Version
 * Coding challenges list with modern UI
 */
function Challenges() {
  const history = useHistory();
  const { config, challenges } = useConfig();
  const { getProgress, isChallengeCompleted } = useProgress();
  const [difficulty, setDifficulty] = useState('all');

  const allChallenges = challenges?.challenges || [];

  const getFilteredChallenges = () => {
    if (difficulty === 'all') return allChallenges;
    return allChallenges.filter(c => c.difficulty === difficulty);
  };

  const getCompletedCount = () => {
    return allChallenges.filter(c => isChallengeCompleted(c.id)).length;
  };

  const getDifficultyConfig = (diff) => {
    const configs = {
      easy: { 
        color: '#4CAF50', 
        gradient: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
        icon: '🌱',
        label: 'Easy'
      },
      medium: { 
        color: '#FF9800', 
        gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
        icon: '🔥',
        label: 'Medium'
      },
      hard: { 
        color: '#F44336', 
        gradient: 'linear-gradient(135deg, #F44336 0%, #E57373 100%)',
        icon: '⚡',
        label: 'Hard'
      }
    };
    return configs[diff] || configs.easy;
  };

  const getCategoryConfig = (category) => {
    const configs = {
      basics: { icon: '📚', label: 'Basics' },
      math: { icon: '🔢', label: 'Math' },
      conditions: { icon: '🔀', label: 'Conditions' },
      loops: { icon: '🔄', label: 'Loops' },
      functions: { icon: '⚙️', label: 'Functions' },
      arrays: { icon: '📊', label: 'Arrays' },
      strings: { icon: '📝', label: 'Strings' }
    };
    return configs[category] || { icon: '💻', label: category || 'General' };
  };

  const handleChallengeClick = (challenge) => {
    history.push(`/challenges/${challenge.id}`);
  };

  const filteredChallenges = getFilteredChallenges();
  const completedCount = getCompletedCount();
  const totalCount = allChallenges.length;
  const progressPercent = totalCount > 0 ? completedCount / totalCount : 0;

  const meta = seoService.getPageMeta('challenges');

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={meta.canonicalUrl}
        ogImage={meta.ogImage}
      />
      <IonPage className="challenges-page">
        <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>Coding Challenges</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="challenges-content">
        {/* Hero Stats Section */}
        <div className="challenges-hero">
          <div className="hero-content">
            <div className="hero-icon">
              <IonIcon icon={trophy} />
            </div>
            <div className="hero-text">
              <h1>Practice Makes Perfect!</h1>
              <p>Complete challenges to master coding</p>
            </div>
          </div>
          
          <div className="stats-row">
            <div className="stat-item">
              <div className="stat-value">{completedCount}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">{totalCount - completedCount}</div>
              <div className="stat-label">Remaining</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">{totalCount}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>

          <div className="progress-section">
            <div className="progress-header">
              <span>Overall Progress</span>
              <span>{Math.round(progressPercent * 100)}%</span>
            </div>
            <IonProgressBar value={progressPercent} color="success"></IonProgressBar>
          </div>
        </div>

        {/* Difficulty Filter */}
        <div className="filter-section">
          <h3>Filter by Difficulty</h3>
          <div className="difficulty-filter">
            <button 
              className={`filter-chip ${difficulty === 'all' ? 'active' : ''}`}
              onClick={() => setDifficulty('all')}
            >
              <span className="chip-icon">🎯</span>
              All
            </button>
            <button 
              className={`filter-chip easy ${difficulty === 'easy' ? 'active' : ''}`}
              onClick={() => setDifficulty('easy')}
            >
              <span className="chip-icon">🌱</span>
              Easy
            </button>
            <button 
              className={`filter-chip medium ${difficulty === 'medium' ? 'active' : ''}`}
              onClick={() => setDifficulty('medium')}
            >
              <span className="chip-icon">🔥</span>
              Medium
            </button>
            <button 
              className={`filter-chip hard ${difficulty === 'hard' ? 'active' : ''}`}
              onClick={() => setDifficulty('hard')}
            >
              <span className="chip-icon">⚡</span>
              Hard
            </button>
          </div>
        </div>

        {/* Challenges List */}
        <div className="challenges-grid">
          {filteredChallenges.map((challenge, index) => {
            const completed = isChallengeCompleted(challenge.id);
            const diffConfig = getDifficultyConfig(challenge.difficulty);
            const catConfig = getCategoryConfig(challenge.category);
            
            return (
              <div 
                key={challenge.id}
                className={`challenge-card ${completed ? 'completed' : ''} ${challenge.difficulty}`}
                onClick={() => handleChallengeClick(challenge)}
              >
                {/* Card Header with Gradient */}
                <div 
                  className="card-header"
                  style={{ background: completed ? 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)' : diffConfig.gradient }}
                >
                  <div className="card-number">#{index + 1}</div>
                  <div className="card-status">
                    {completed ? (
                      <IonIcon icon={checkmarkCircle} className="completed-icon" />
                    ) : (
                      <span className="difficulty-emoji">{diffConfig.icon}</span>
                    )}
                  </div>
                </div>

                {/* Card Body */}
                <div className="card-body">
                  <div className="card-category">
                    <span className="category-icon">{catConfig.icon}</span>
                    <span className="category-label">{catConfig.label}</span>
                  </div>

                  <h3 className="card-title">{challenge.title}</h3>
                  <p className="card-description">{challenge.description}</p>

                  <div className="card-footer">
                    <div className="card-meta">
                      <span 
                        className={`difficulty-badge ${challenge.difficulty}`}
                      >
                        {diffConfig.label}
                      </span>
                      <span className="points-badge">
                        <IonIcon icon={star} />
                        {challenge.points} XP
                      </span>
                    </div>

                    <div className="card-action">
                      {completed ? (
                        <span className="action-text completed">✓ Done</span>
                      ) : (
                        <span className="action-text">Solve →</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Completed Overlay */}
                {completed && (
                  <div className="completed-ribbon">
                    <span>Completed</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredChallenges.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No Challenges Found</h3>
            <p>Try selecting a different difficulty level</p>
            <button 
              className="reset-filter-btn"
              onClick={() => setDifficulty('all')}
            >
              Show All Challenges
            </button>
          </div>
        )}

        {/* Motivational Footer */}
        {filteredChallenges.length > 0 && completedCount < totalCount && (
          <div className="motivational-footer">
            <IonIcon icon={sparkles} />
            <span>Keep going! You're doing great!</span>
            <IonIcon icon={sparkles} />
          </div>
        )}

        {completedCount === totalCount && totalCount > 0 && (
          <div className="all-complete-footer">
            <div className="celebration-icon">🎉</div>
            <h3>Congratulations!</h3>
            <p>You've completed all challenges!</p>
          </div>
        )}
      </IonContent>
    </IonPage>
    </>
  );
}

export default Challenges;